<template>
  <div id="pulsePage">
    <!-- Responsive layout -->
    <div
      :class="['desktop', 'laptop'].includes($mq) ? 'pageDesk' : 'pageMobile'"
    >
      <div class="customPagination">
        <!-- Pagination hidden -->
        <el-pagination
          layout="prev, pager, next"
          :next-text="['desktop', 'laptop'].includes($mq) ? 'Next' : ''"
          :prev-text="['desktop', 'laptop'].includes($mq) ? 'Prev' : ''"
          :current-page="scene"
          :page-size="1"
          :total="7"
          @current-change="currentPage"
        />
        <!-- Visual progress but placed under the pagination -->
        <el-progress
          :percentage="percentageFilter"
          :color="'#00ace2'"
          :show-text="false"
        />
      </div>

      <Carousel
        :per-page="1"
        :navigate-to="scene - 1"
        :mouse-drag="['mobile', 'tablet'].includes($mq)"
        :touch-drag="['mobile', 'tablet'].includes($mq)"
        @page-change="currentPage($event + 1)"
      >
        <Slide
          v-for="(pageData, pageKey) in pageStructure"
          :key="pageKey"
        >
          <div
            :key="pageKey"
            class="eachPage"
            :class="`${pageKey < scene - 1 || scene - 1 < pageKey ? 'outRange' : ''}`"
          >
            <!-- Navigator -->
            <div class="title">
              <i18n path="pulse.survey.question.mainTitle">
                <template #leader>
                  {{ leaderFullName }}
                </template>
                <template #startDate>
                  {{ feedforwardStart }}
                </template>
              </i18n>
            </div>

            <!-- Description with leader data -->
            <div
              class="description"
              :class="pageSource.blankList.includes(`q${pageData.question}`) ? 'redAlert' : ''"
            >
              <div v-if="role === 'leader'">
                {{ $t(`pulse.survey.question.q${pageData.question}.leader`) }}
              </div>
              <div v-else>
                <i18n :path="`pulse.survey.question.q${pageData.question}.coworker`">
                  <template #leader>
                    {{ pulseData.leader_first_name }}
                  </template>
                </i18n>
              </div>
            </div>

            <!-- Question list divide by type -->
            <div class="questionWrap">
              <!-- Title for lga question -->
              <div
                v-if="pageData.title"
                class="questionTitle"
                :class="pageSource.blankList.includes(`q${pageData.question}`) ? 'redAlert' : ''"
              >
                <span v-if="['1a', '1b'].includes(pageData.question) && pulseData.lga">
                  <b>
                    {{ pageData.question === '1a'
                      ? `A. ${pulseData.lga.lga1}` : `B. ${pulseData.lga.lga2}` }}
                  </b>
                </span>
                <span v-else>
                  {{ pageData.title }}
                </span>
              </div>

              <!-- LGA Step type -->
              <div
                v-if="pageData.type === 'step.lga' && pageSource"
                class="questionSelect lgaStep"
              >
                <!-- Desk mode = step -->
                <el-steps
                  v-if="['desktop', 'laptop'].includes($mq)"
                  :active="pageSource.step.key[`q${pageData.question}`] === 0 ?
                    5 : pageSource.step.key[`q${pageData.question}`] - 1"
                  align-center
                >
                  <el-step
                    v-for="(item, stepKey) in pageSource.step.lga"
                    :id="'customStep' + item.score"
                    :key="stepKey"
                    :title="item.desc"
                    :class="{
                      redAlert: pageSource.blankList.includes(`q${pageData.question}`),
                      noFollowingLine: item.score === 5
                    }"
                  >
                    <template slot="icon">
                      <button
                        class="iconButton"
                        @click="selected(
                          `q${pageData.question}`,
                          item.score,
                          item.desc,
                          pageData.page
                        )"
                      >
                        <span v-if="item.score === 0">
                          NN
                        </span>
                        <span v-else>
                          {{ item.score }}
                        </span>
                      </button>
                    </template>
                  </el-step>
                </el-steps>

                <!-- Mobile mode = radio -->
                <el-radio-group
                  v-else
                  v-model="pageSource.answer[`q${pageData.question}`]"
                  @change="radioToKey(`q${pageData.question}`, $event)"
                >
                  <el-radio-button
                    v-for="(item, key) in pageSource.step.lga"
                    :key="key"
                    :label="item.score"
                    :class="pageSource.blankList.includes(`q${pageData.question}`)
                      ? 'redAlert' : ''"
                  >
                    <div class="radioScore">
                      <i
                        v-if="item.score > 7"
                        class="el-icon-check"
                      ></i>
                      <span v-else>
                        {{ item.score }}
                      </span>
                    </div>
                    <div class="radioDesc">
                      {{ item.desc }}
                    </div>
                  </el-radio-button>
                </el-radio-group>
              </div>
              <!-- /LGA step type -->

              <!-- Plan step -->
              <div
                v-else-if="pageData.type === 'step.plan' && pageSource"
                class="questionSelect"
              >
                <el-steps
                  v-if="['desktop', 'laptop'].includes($mq)"
                  :active="pageSource.step.key[`q${pageData.question}`] === 0 ?
                    5 : pageSource.step.key[`q${pageData.question}`] - 1"
                  align-center
                >
                  <!-- Deskmode = step -->
                  <el-step
                    v-for="(item, stepKey) in pageSource.step.plan"
                    :id="'customStep' + item.score"
                    :key="stepKey"
                    :title="item.desc"
                    :class="pageSource.blankList.includes(`q${pageData.question}`)
                      ? 'redAlert' : ''"
                  >
                    <template slot="icon">
                      <button
                        class="iconButton"
                        @click="selected(
                          `q${pageData.question}`,
                          item.score,
                          item.desc,
                          pageData.page
                        )"
                      >
                        {{ item.score }}
                      </button>
                    </template>
                  </el-step>
                </el-steps>

                <!-- Mobile mode = radio -->
                <el-radio-group
                  v-else
                  v-model="pageSource.answer[`q${pageData.question}`]"
                  @change="radioToKey(`q${pageData.question}`, $event)"
                >
                  <el-radio-button
                    v-for="(item, key) in pageSource.step.plan"
                    :key="key"
                    :label="item.score"
                    :class="pageSource.blankList.includes(`q${pageData.question}`)
                      ? 'redAlert' : ''"
                  >
                    <div class="radioScore">
                      <i class="el-icon-check"></i>
                    </div>
                    <div class="radioDesc">
                      {{ item.desc }}
                    </div>
                  </el-radio-button>
                </el-radio-group>
              </div>
              <!-- /Plan step -->

              <!-- Leadership type -->
              <div
                v-else-if="pageData.type === 'step.leadership' && pageSource"
                class="questionSelect"
              >
                <el-steps
                  v-if="['desktop', 'laptop'].includes($mq)"
                  :active="pageSource.step.key[`q${pageData.question}`] === 0 ?
                    5 : pageSource.step.key[`q${pageData.question}`] - 1"
                  align-center
                >
                  <!-- Deskmode = step -->
                  <el-step
                    v-for="(item, stepKey) in pageSource.step.leadership"
                    :id="'customStep' + item.score"
                    :key="stepKey"
                    :title="item.desc"
                    :class="{
                      redAlert: pageSource.blankList.includes(`q${pageData.question}`),
                      noFollowingLine: item.score === 5
                    }"
                  >
                    <template slot="icon">
                      <button
                        class="iconButton"
                        @click="selected(
                          `q${pageData.question}`,
                          item.score,
                          item.desc,
                          pageData.page
                        )"
                      >
                        <span v-if="item.score === 0">
                          NN
                        </span>
                        <span v-else>
                          {{ item.score }}
                        </span>
                      </button>
                    </template>
                  </el-step>
                </el-steps>

                <!-- Mobile mode = radio -->
                <el-radio-group
                  v-else
                  v-model="pageSource.answer[`q${pageData.question}`]"
                  @change="radioToKey(`q${pageData.question}`, $event)"
                >
                  <el-radio-button
                    v-for="(item, key) in pageSource.step.leadership"
                    :key="key"
                    :label="item.score"
                    :class="pageSource.blankList.includes(`q${pageData.question}`)
                      ? 'redAlert' : ''"
                  >
                    <div class="radioScore">
                      <i class="el-icon-check"></i>
                    </div>
                    <div class="radioDesc">
                      {{ item.desc }}
                    </div>
                  </el-radio-button>
                </el-radio-group>
              </div>
              <!-- /Leadership type -->

              <!-- Text area type -->
              <div
                v-else-if="pageData.type === 'textarea' && pageSource"
                class="questionSelect"
              >
                <div class="questionText">
                  <el-input
                    v-model="pageSource.answer[`q${pageData.question}`]"
                    class="questionArea"
                    type="textarea"
                    placeholder=""
                    maxlength="3000"
                    rows="8"
                    show-word-limit
                  />
                  <el-button
                    v-if="pageData.page === 7"
                    class="textButton"
                    type="text"
                    @click="submitPulse"
                  >
                    Done
                  </el-button>
                  <!--<el-button
                    v-else
                    class="textButton"
                    type="text"
                    @click="currentPage(pageData.page + 1)"
                  >
                    Next
                  </el-button>-->
                </div>
              </div>
              <!-- /Text area type -->
            </div>
          </div>
        </Slide>
      </Carousel>
    </div>
    <!-- /Responsive layout -->
  </div>
</template>

<script>
import { Carousel, Slide } from 'vue-carousel';

export default {
  components: {
    Carousel,
    Slide,
  },
  props: {
    role: {
      type: String,
      default: 'stakeholder',
    },
    pulseData: {
      type: Object,
      default: null,
    },
    scene: {
      type: Number,
      default: 1,
    },
    submit: {
      type: Boolean,
      default: false,
    },
    pageStructure: {
      type: Array,
      default: null,
    },
    pageSource: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    leaderFullName() {
      if (this.pulseData) {
        return `${this.pulseData.leader_first_name} ${this.pulseData.leader_last_name}`;
      }
      return '';
    },

    feedforwardStart() {
      if (this.pulseData && this.pulseData.first_ff_date) {
        return this.$moment(this.pulseData.first_ff_date).format('MMMM YYYY');
      }
      return '';
    },

    percentageFilter() {
      let percent = 0;

      if (this.scene) {
        const value = Math.round(this.scene * 14.3);

        if (value > 100) {
          percent = 100;
        } else if (value < 0) {
          percent = 0;
        } else {
          percent = value;
        }
      }

      return percent;
    },
  },

  methods: {
    submitPulse() {
      this.$emit('submitPulse');
    },

    selected(option, value, desc, page) {
      // Question, Step, Answer
      if (['q1a', 'q1b'].includes(option)) {
        this.pageSource.step.key[option] = value;
        this.pageSource.answer[option] = value;
      } else {
        this.pageSource.step.key[option] = value;
        this.pageSource.answer[option] = value;
      }

      if (!this.pageSource.firstTry) {
        this.$emit('validationPulse');
      }

      // Delayed auto page scroll
      setTimeout(() => {
        this.currentPage(page + 1);
      }, 400);
    },

    radioToKey(option, score) {
      if (['q1a', 'q1b'].includes(option)) {
        this.pageSource.step.key[option] = score + 3;
        this.pageSource.answer[option] = score;
      } else {
        this.pageSource.step.key[option] = score;
        this.pageSource.answer[option] = score;
      }

      if (!this.pageSource.firstTry) {
        this.$emit('validationPulse');
      }

      // Delayed auto page scroll
      setTimeout(() => {
        this.$emit('update:scene', this.scene + 1);
      }, 400);
    },

    currentPage(page) {
      // Pagination with number params
      this.$emit('update:scene', page);
    },
  },
};
</script>

<style lang="scss" scoped>
#pulsePage {
  .pageDesk {
    position: relative;

    .VueCarousel {
      padding-bottom: 40px;
    }

    .VueCarousel-slide {
      padding: 5px 10px;
      box-sizing: border-box;
    }

    .title {
      font-style: italic;
      font-weight: 500;
      font-size: 16px;
      line-height: 20px;
    }

    .description {
      font-size: 15px;
      padding: 25px 0 5px;

      &.redAlert {
        color: red;
      }
    }

    .questionWrap {
      padding: 15px 0;

      .questionTitle {
        padding: 10px 0;

        &.redAlert {
          color: red;
        }
      }

      .questionSelect {
        .el-step {
          width: 10%;
          padding: 15px 0 10px;

          .iconButton {
            background: transparent;
            border: none;
            width: 100%;
            height: 100%;
            border-radius: 100%;
            font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
            font-size: 15px;
            color: #9c9c9c;
            cursor: pointer;
            padding: 0;

            &:focus {
              outline: none;
            }

            .iconSymbol {
              width: 100%;
              font-weight: bold;
              font-size: 18px;
              margin-top: 2px;
            }
          }
        }
      }

      .questionText {
        position: relative;

        .questionArea {
          position: relative;
          top: 0;
          left: 0;
          border-radius: 5px;
          box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
        }

        .textButton {
          position: absolute;
          width: 60px;
          font-weight: bold;
          background: white;
          bottom: 0;
          right: 0;
        }
      }
    }

    .customPagination {
      position: absolute;
      width: 100%;
      bottom: -20px;
    }
  }

  .pageMobile {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background: white;

    .VueCarousel-slide {
      padding: 5px 10px;
      box-sizing: border-box;
    }

    .title {
      font-style: italic;
      font-weight: 500;
      font-size: 16px;
      line-height: 20px;
    }

    .sceneWrap {
      position: relative;
    }

    .eachPage {
      padding: 0 20px 20px;
      box-sizing: border-box;

      .description {
        font-size: 14px;
        padding: 10px 0;

        &.redAlert {
          color: red;
        }
      }

      .questionWrap {
        padding: 15px 0;

        .questionTitle {
          padding: 10px 0;

          &.redAlert {
            color: red;
          }
        }

        .questionText {
          position: relative;

          .questionArea {
            position: relative;
            top: 0;
            left: 0;
            border-radius: 5px;
            border: 1px solid #00ace2;
          }

          .textButton {
            position: absolute;
            width: 60px;
            font-weight: bold;
            bottom: 0;
            right: 0;
          }
        }
      }

      &.outRange {
        height: 10px;
        min-height: calc(100vh - 100px);
      }
    }
  }
}
</style>

<style lang="scss">
  #pulsePage {
    .VueCarousel-pagination {
      display: none !important;
    }

    .pageDesk {
      .questionWrap {
        .questionSelect {
          .el-step {
            .el-step__title {
              color: black !important;
              line-height: normal;
              margin: 10px auto 0;
              font-size: 12px;
              word-break: break-word;
              width: calc(100% - 5px);
            }

            .el-step__icon {
              width: 40px;
              height: 40px;
              border-width: 2px;
              border-color: #00ace2;
              background-color: transparent;
              font-weight: bold;
              color: #9c9c9c;
              cursor: pointer;
            }

            // Global step head, line
            .el-step__head {
              .el-step__line {
                top: 19px;
                height: 2px;
                background: #00ace2;
                left: calc(50% + 19px);
                right: calc(-50% + 19px);
                border-radius: 10px;

                .el-step__line-inner {
                  border-width: 1px !important;
                  border-radius: 10px;
                  display: none;
                }
              }
            }

            &.redAlert {
              .el-step__icon {
                border-color: red;
              }

              .el-step__head {
                .el-step__line {
                  background: red;
                }
              }
            }

            &.noFollowingLine {
              .el-step__line {
                display: none;
              }
            }

            // Process step head, line
            .el-step__head.is-process {
              .el-step__icon {
                background-color: #00ace2;
                border-width: 4px;
                color: white;
              }

              .iconButton {
                font-weight: bold;
                color: white;
              }
            }
          }

          &.lgaStep {
            .el-step:nth-child(7) {
              .el-step__line {
                display: none;
              }
            }
          }
        }

        .questionText {
          .questionArea {
            .el-textarea__inner {
              position: relative;
              border: none;
              padding: 15px 15px 0;
              margin-bottom: 40px;
              resize: none;
            }

            .el-input__count {
              left: 0 !important;
              bottom: 0;
              border-radius: 5px;
              padding: 14px;
              right: 0;
            }
          }
        }
      }

      .customPagination {
        .el-pagination {
          display: flex;
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          justify-content: center;
          z-index: 2000;
          padding: 5px 0 15px;

          .el-pager {
            width: 81%;

            .number {
              font-size: 0;
              min-width: 1px;
              min-height: 1px;
              width: 14.2%;
            }
          }

          button {
            color: #00ace2 !important;
            padding: 0 10px;

            &.btn-prev {
              padding-left: 0;
            }

            &.btn-next {
              padding-right: 0;
            }

            &:disabled {
              color: #a1a1a1 !important;
            }

            .el-icon {
              font-size: 22px;
            }

            span {
              font-size: 16px;
              line-height: 28px;
            }
          }
        }

        .el-progress {
          left: 0;
          bottom: 0;
          width: 100%;
          z-index: 1800;
          padding: 26px 0;

          .el-progress-bar {
            width: 80%;
            margin: 0 auto;

            .el-progress-bar__outer {
              background-color: #c4c4c4;
            }
          }
        }
      }
    }

    .pageMobile {
      .eachPage {
        .questionWrap {
          .questionSelect {
            .el-radio-group {
              .el-radio-button {
                width: 100%;
                padding: 3px 0;

                &:focus {
                  outline: none !important;
                  box-shadow: none !important;
                }

                .el-radio-button__inner {
                  position: relative;
                  width: 100%;
                  padding: 15px;
                  border-radius: 5px;
                  border: 1px solid #00ace2;
                  font-size: 14px;
                  text-align: left;

                  .radioScore {
                    display: inline-block;
                    width: 22px;
                  }

                  .radioDesc {
                    display: inline-block;
                    color: black;
                    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
                  }

                  .radioActive {
                    position: absolute;
                    display: inline-block;
                    right: 10px;
                    visibility: hidden;

                    i {
                      font-size: 20px;
                    }
                  }
                }

                &.redAlert {
                  .el-radio-button__inner {
                    border: 1px solid red;
                  }
                }

                &.is-active {
                  .el-radio-button__inner {
                    background: #00ace2;

                    .radioDesc {
                      color: white;
                    }

                    .radioActive {
                      visibility: visible;
                    }
                  }
                }
              }
            }
          }

          .questionText {
            .questionArea {
              .el-textarea__inner {
                position: relative;
                border: none;
                padding: 15px 15px 0;
                margin-bottom: 40px;
                resize: none;
              }

              .el-input__count {
                left: 0 !important;
                bottom: 0;
                border-radius: 5px;
                padding: 14px;
                right: 0;
              }
            }
          }
        }
      }

      .customPagination {
        position: relative;
        top: 0;

        .el-pagination {
          display: flex;
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          justify-content: center;
          z-index: 2000;
          padding: 5px 0 15px;

          .el-pager {
            width: 85%;

            .number {
              font-size: 0;
              min-width: 1px;
              min-height: 1px;
              width: 14.2%;
            }
          }

          .btn-prev,
          .btn-next {
            color: #00ace2 !important;
            padding: 0 10px;

            &:disabled {
              color: #a1a1a1 !important;
            }

            .el-icon {
              font-size: 22px;
            }
          }
        }

        .el-progress {
          left: 0;
          bottom: 0;
          width: 100%;
          z-index: 1800;
          padding: 26px 0;
          background: white;

          .el-progress-bar {
            width: 84%;
            margin: 0 auto;
          }
        }
      }
    }
  }
</style>
